import { useState, useCallback, useRef, useEffect } from 'react';

const COPY_FEEDBACK_MS = 2000;

/**
 * Copy text to the clipboard with a temporary "copied" feedback state.
 *
 * Uses the Clipboard API with an execCommand fallback for HTTP contexts.
 * Returns `{ copy, copied }` — `copied` is true for 2 seconds after a successful copy.
 */
export function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const copy = useCallback(async (text: string) => {
    // Clipboard API (requires HTTPS or localhost)
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
        return;
      } catch {
        // Fall through to execCommand
      }
    }

    // Fallback for HTTP contexts
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.cssText = 'position:fixed;left:0;top:0;opacity:0;';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);

    if (success) {
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    }
  }, []);

  const reset = useCallback(() => {
    clearTimeout(timerRef.current);
    setCopied(false);
  }, []);

  // F19: cancel any in-flight "copied=false" timer on unmount so the host
  // doesn't see a setCopied call after it has gone away.
  useEffect(() => {
    return () => {
      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return { copy, copied, reset } as const;
}
